// src/api/index.js

/**
 * 운영 배포(nginx 프록시) 정석:
 * - 브라우저에서는 API_BASE_URL = "/api"
 *   -> https://oz701.com/api/... 로 요청
 *   -> nginx가 내부 127.0.0.1:4000 으로 프록시
 *
 * 로컬 개발:
 * - .env 에 REACT_APP_API_BASE_URL=http://localhost:4000/api 설정하면 그대로 사용
 * - 설정이 없으면 기본값은 브라우저에서 "/api" 로 동작
 */

const DEFAULT_API_BASE_URL = (() => {
  // 브라우저(React 실행 환경)
  if (typeof window !== 'undefined') {
    return '/api';
  }
  // SSR/테스트 등 Node 환경(거의 안 씀)
  return 'http://localhost:4000/api';
})();

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL)
  .replace(/\/+$/, ''); // 끝 슬래시 제거

const SESSION_EXPIRED_EVENT = 'auth:expired';

function notifySessionExpired() {
  if (typeof window === 'undefined') return;
  if (window.__authExpiredNotified) return;
  window.__authExpiredNotified = true;
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

export function getServerBaseUrl() {
  // 기존 코드 호환용:
  // API_BASE_URL이 "/api" 같은 상대경로면 서버 base url은 "" 로 보는 게 안전
  if (API_BASE_URL.startsWith('/')) return '';
  return API_BASE_URL.replace(/\/api\/?$/, '');
}

async function request(path, options = {}) {
  const { headers: customHeaders, ...restOptions } = options;

const res = await fetch(`${API_BASE_URL}${path}`, {
  ...restOptions,
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
    ...(customHeaders || {}),
  },
});

  const data = await res.json().catch(() => ({}));

  if (res.status === 401) {
    notifySessionExpired();
    const error = new Error(data.message || '로그인을 새로 해주세요.');
    error.status = res.status;
    error.data = data;
    error.suppressAlert = true;
    throw error;
  }

  if (!res.ok) {
    const message = data.message || '요청 중 오류가 발생했습니다.';
    const error = new Error(message);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

export async function checkLoginIdApi(loginId) {
  return request('/auth/check-login-id', {
    method: 'POST',
    body: JSON.stringify({ loginId }),
  });
}

export async function registerUserApi({ name, loginId, password }) {
  return request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ name, loginId, password }),
  });
}

export async function loginUserApi({ loginId, password }) {
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ loginId, password }),
  });
}

export async function changePasswordApi({ currentPassword, newPassword }) {
  const token = window.localStorage.getItem('accessToken');

  return request('/auth/change-password', {
    method: 'POST',
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function verifyPasswordApi({ currentPassword }) {
  const token = window.localStorage.getItem('accessToken');

  return request('/auth/verify-password', {
    method: 'POST',
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
    body: JSON.stringify({ currentPassword }),
  });
}

export async function changeNameApi({ newName }) {
  const token = window.localStorage.getItem('accessToken');

  return request('/auth/change-name', {
    method: 'POST',
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
    body: JSON.stringify({ newName }),
  });
}

export async function getAdminUsersApi({ keyword, role, status, page, limit, sort } = {}) {
  const token = window.localStorage.getItem('accessToken');
  const params = new URLSearchParams();
  if (keyword) params.append('keyword', keyword);
  if (role) params.append('role', role);
  if (status) params.append('status', status);
  if (page) params.append('page', page);
  if (limit) params.append('limit', limit);
  if (sort) params.append('sort', sort);

  const query = params.toString();
  const path = query ? `/admin/users?${query}` : '/admin/users';

  return request(path, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getAdminAdminsApi() {
  const token = window.localStorage.getItem('accessToken');
  return request('/admin/users/admins', {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function updateAdminViewBoostApi(userId, viewBoost) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/admin/users/admins/${userId}/view-boost`, {
    method: 'POST',
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
    body: JSON.stringify({ viewBoost }),
  });
}

export async function adminBlacklistRegisterApi(userIds = []) {
  const token = window.localStorage.getItem('accessToken');
  return request('/admin/users/blacklist/register', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ userIds }),
  });
}

export async function adminBlacklistReleaseApi(userIds = []) {
  const token = window.localStorage.getItem('accessToken');
  return request('/admin/users/blacklist/release', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ userIds }),
  });
}

export async function adminResetUserPasswordApi(userId, newPassword) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/admin/users/${userId}/password/reset`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ newPassword }),
  });
}

export async function getAdminStatisticsApi({ date, month } = {}) {
  const token = window.localStorage.getItem('accessToken');
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (month) params.append('month', month);
  const query = params.toString();
  const path = query ? `/admin/statistics?${query}` : '/admin/statistics';

  return request(path, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getAdminServiceLinksApi() {
  const token = window.localStorage.getItem('accessToken');
  return request('/admin/service-links', {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function updateAdminServiceLinksApi(links) {
  const token = window.localStorage.getItem('accessToken');
  return request('/admin/service-links', {
    method: 'PUT',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ links }),
  });
}

export async function getServiceLinksApi() {
  return request('/service-links', { method: 'GET' });
}

export async function getInboxListApi({ keyword, page, limit } = {}) {
  const token = window.localStorage.getItem('accessToken');
  const params = new URLSearchParams();
  if (keyword) params.append('keyword', keyword);
  if (page) params.append('page', page);
  if (limit) params.append('limit', limit);

  const query = params.toString();
  const path = query ? `/inbox?${query}` : '/inbox';

  return request(path, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getAdminInboxUnreadCountApi() {
  const token = window.localStorage.getItem('accessToken');
  return request('/inbox/admin/unread-count', {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function initInboxThreadApi() {
  const token = window.localStorage.getItem('accessToken');

  return request('/inbox', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getAdminInboxThreadByUserIdApi(userId) {
  const token = window.localStorage.getItem('accessToken');
  const params = new URLSearchParams();
  if (userId) params.append('userId', userId);
  const query = params.toString();
  const path = query ? `/inbox/admin/thread?${query}` : '/inbox/admin/thread';

  return request(path, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getInboxThreadApi(threadId) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/inbox/${threadId}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function sendMessageApi(threadId, content) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/inbox/${threadId}/messages`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ content }),
  });
}

export async function deleteMessageApi(threadId, messageId) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/inbox/${threadId}/messages/${messageId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function deleteInboxThreadApi(threadId) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/inbox/${threadId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getLatestNewsApi() {
  return request('/news/latest', { method: 'GET' });
}

export async function createNewsApi(payload) {
  const token = window.localStorage.getItem('accessToken');

  return request('/news', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify(payload),
  });
}

export async function uploadNewsImageApi(formData) {
  const token = window.localStorage.getItem('accessToken');
  const res = await fetch(`${API_BASE_URL}/news/image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    notifySessionExpired();
    const error = new Error(data.message || '로그인을 새로 해주세요.');
    error.status = res.status;
    error.data = data;
    error.suppressAlert = true;
    throw error;
  }
  if (!res.ok) {
    throw new Error(data.message || 'Image upload failed');
  }
  return data;
}

export async function uploadNewsVideoApi(formData) {
  const token = window.localStorage.getItem('accessToken');
  const res = await fetch(`${API_BASE_URL}/news/video`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    notifySessionExpired();
    const error = new Error(data.message || '로그인을 새로 해주세요.');
    error.status = res.status;
    error.data = data;
    error.suppressAlert = true;
    throw error;
  }
  if (!res.ok) {
    const message = data.message || 'Video upload failed';
    const error = new Error(message);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

export async function cleanupNewsUploadsApi(files = []) {
  if (!Array.isArray(files) || files.length === 0) return { cleaned: 0 };
  const token = window.localStorage.getItem('accessToken');
  return request('/news/uploads/cleanup', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ files }),
  });
}

export async function getNewsListApi({ header, keyword, filterType, sort, page, limit } = {}) {
  const params = new URLSearchParams();
  if (header) params.append('header', header);
  if (keyword) params.append('keyword', keyword);
  if (filterType) params.append('filterType', filterType);
  if (sort) params.append('sort', sort);
  if (page) params.append('page', String(page));
  if (limit) params.append('limit', String(limit));

  const query = params.toString();
  const path = query ? `/news?${query}` : '/news';

  return request(path, { method: 'GET' });
}

export async function getNewsDetailApi(id) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/news/${id}`, {
    method: 'GET',
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
  });
}

export async function updateNewsApi(id, payload) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/news/${id}`, {
    method: 'PATCH',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify(payload),
  });
}

export async function deleteNewsApi(id) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/news/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getNewsCommentsApi(newsId, { page, limit } = {}) {
  const params = new URLSearchParams();
  if (newsId) params.append('news', newsId);
  if (page) params.append('page', String(page));
  if (limit) params.append('limit', String(limit));

  const query = params.toString();
  const path = query ? `/news/comments?${query}` : '/news/comments';
  const token = window.localStorage.getItem('accessToken');

  return request(path, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function createNewsCommentApi(payload) {
  const token = window.localStorage.getItem('accessToken');

  return request('/news/comments', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify(payload),
  });
}

export async function deleteNewsCommentApi(id) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/news/comments/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function updateNewsCommentApi(id, content, displayName = null) {
  const token = window.localStorage.getItem('accessToken');

  const payload = { content };
  if (displayName !== null) {
    payload.displayName = displayName;
  }

  return request(`/news/comments/${id}`, {
    method: 'PATCH',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify(payload),
  });
}

export async function likeNewsCommentApi(id) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/news/comments/${id}/like`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function unlikeNewsCommentApi(id) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/news/comments/${id}/like`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function boostNewsCommentLikeApi(id) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/news/comments/${id}/like/boost`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getNewsRecommendStatusApi(newsId) {
  const token = window.localStorage.getItem('accessToken');
  const params = new URLSearchParams();
  if (newsId) params.append('news', newsId);

  const query = params.toString();
  const path = query ? `/news/recommends/status?${query}` : '/news/recommends/status';

  return request(path, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getPesoExchangeRateApi({ force } = {}) {
  const params = new URLSearchParams();
  if (force) params.append('force', 'true');
  const query = params.toString();
  const path = query ? `/exchange/peso?${query}` : '/exchange/peso';

  return request(path, { method: 'GET' });
}

export async function addNewsRecommendApi(newsId) {
  const token = window.localStorage.getItem('accessToken');

  return request('/news/recommends', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ news: newsId }),
  });
}

export async function boostNewsRecommendApi(newsId) {
  const token = window.localStorage.getItem('accessToken');

  return request('/news/recommends/boost', {
    method: 'POST',
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
    body: JSON.stringify({ news: newsId }),
  });
}

export async function removeNewsRecommendApi(newsId) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/news/recommends/${newsId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getNewsHeadersApi() {
  return request('/news/headers', { method: 'GET' });
}

export async function createNewsHeaderApi(payload) {
  const token = window.localStorage.getItem('accessToken');

  return request('/news/headers', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify(payload),
  });
}

export async function updateNewsHeaderApi(id, payload) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/news/headers/${id}`, {
    method: 'PATCH',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify(payload),
  });
}

export async function createShopApi(formData) {
  const token = window.localStorage.getItem('accessToken');

  const res = await fetch(`${API_BASE_URL}/shops`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    notifySessionExpired();
    const error = new Error(data.message || '로그인을 새로 해주세요.');
    error.status = res.status;
    error.data = data;
    error.suppressAlert = true;
    throw error;
  }

  if (!res.ok) {
    const message = data.message || '요청 중 오류가 발생했습니다.';
    const error = new Error(message);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

export async function getShopCategoriesApi() {
  return request('/shop-categories', {
    method: 'GET',
  });
}

export async function createShopCategoryApi(payload) {
  const token = window.localStorage.getItem('accessToken');

  return request('/shop-categories', {
    method: 'POST',
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
    body: JSON.stringify(payload),
  });
}

export async function updateShopCategoryApi(id, payload) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/shop-categories/${id}`, {
    method: 'PATCH',
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
    body: JSON.stringify(payload),
  });
}

export async function getShopListApi({ categoryId, page = 1, limit = 20, sort } = {}) {
  const params = new URLSearchParams();
  if (categoryId) params.append('categoryId', String(categoryId));
  if (page) params.append('page', String(page));
  if (limit) params.append('limit', String(limit));
  if (sort) params.append('sort', String(sort));

  const query = params.toString();
  const path = query ? `/shops?${query}` : '/shops';

  return request(path, {
    method: 'GET',
  });
}

export async function getShopDetailApi(id) {
  return request(`/shops/${id}`, { method: 'GET' });
}

export async function updateShopApi(id, payload) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/shops/${id}`, {
    method: 'PATCH',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify(payload),
  });
}

export async function deleteShopApi(id) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/shops/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function getShopReviewsApi({ shopId, page = 1, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (shopId) params.append('shop', String(shopId));
  if (page) params.append('page', String(page));
  if (limit) params.append('limit', String(limit));

  const query = params.toString();
  const path = query ? `/shop-reviews?${query}` : '/shop-reviews';

  return request(path, { method: 'GET' });
}

export async function createShopReviewApi({ shopId, rating, content, photos, writerName }) {
  const token = window.localStorage.getItem('accessToken');

  const body = {
    shopId,
    rating,
    content,
    photos,
  };
  if (writerName !== null && writerName !== undefined) {
    body.writerName = writerName;
  }

  return request('/shop-reviews', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify(body),
  });
}

export async function updateShopReviewApi(reviewId, { rating, content, photos, writerName }) {
  const token = window.localStorage.getItem('accessToken');

  const body = {
    rating,
    content,
    photos,
  };
  if (writerName !== null && writerName !== undefined) {
    body.writerName = writerName;
  }

  return request(`/shop-reviews/${reviewId}`, {
    method: 'PATCH',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify(body),
  });
}

export async function deleteShopReviewApi(reviewId) {
  const token = window.localStorage.getItem('accessToken');

  return request(`/shop-reviews/${reviewId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

// 엔터테인먼트 업체 후기 이미지 업로드
export async function uploadShopReviewImageApi(formData) {
  const token = window.localStorage.getItem('accessToken');

  const res = await fetch(`${API_BASE_URL}/shop-reviews/image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    notifySessionExpired();
    const error = new Error(data.message || '로그인을 새로 해주세요.');
    error.status = res.status;
    error.data = data;
    error.suppressAlert = true;
    throw error;
  }
  if (!res.ok) {
    const message = data.message || 'Image upload failed';
    const error = new Error(message);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

export async function cleanupShopReviewUploadsApi(files = []) {
  if (!Array.isArray(files) || files.length === 0) return { cleaned: 0 };
  const token = window.localStorage.getItem('accessToken');
  return request('/shop-reviews/uploads/cleanup', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ files }),
  });
}

export async function createShopPhotoApi({ shopId, sortOrder, file }) {
  if (!shopId || !file) {
    const err = new Error('shopId와 파일은 필수입니다.');
    err.status = 400;
    throw err;
  }

  const token = window.localStorage.getItem('accessToken');
  const formData = new FormData();
  formData.append('shopId', shopId);
  if (sortOrder !== undefined && sortOrder !== null) {
    formData.append('sortOrder', String(sortOrder));
  }
  formData.append('photo', file);

  const res = await fetch(`${API_BASE_URL}/shop-photos`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    notifySessionExpired();
    const error = new Error(data.message || '로그인을 새로 해주세요.');
    error.status = res.status;
    error.data = data;
    error.suppressAlert = true;
    throw error;
  }
  if (!res.ok) {
    const message = data.message || '요청 중 오류가 발생했습니다.';
    const error = new Error(message);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

export async function getLinkGroupsApi() {
  return request('/link-groups', { method: 'GET' });
}

export async function getLinkGroupApi(groupId) {
  return request(`/link-groups/${groupId}`, { method: 'GET' });
}

export async function getLinkGroupItemsApi({ groupId } = {}) {
  const params = new URLSearchParams();
  if (groupId) params.append('group', String(groupId));
  const query = params.toString();
  const path = query ? `/link-group-items?${query}` : '/link-group-items';
  return request(path, { method: 'GET' });
}

export async function createLinkGroupApi({ name, iconName, sortOrder } = {}) {
  const token = window.localStorage.getItem('accessToken');
  return request('/link-groups', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ name, iconName, sortOrder }),
  });
}

export async function deleteLinkGroupApi(groupId) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/link-groups/${groupId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export async function updateLinkGroupApi(groupId, { name, iconName, sortOrder } = {}) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/link-groups/${groupId}`, {
    method: 'PATCH',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ name, iconName, sortOrder }),
  });
}

export async function createLinkGroupItemsApi({ groupId, items } = {}) {
  const token = window.localStorage.getItem('accessToken');
  return request('/link-group-items', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ groupId, items }),
  });
}

export async function updateLinkGroupItemApi(itemId, { groupId, title, url, rankLabel, sortOrder } = {}) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/link-group-items/${itemId}`, {
    method: 'PATCH',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: JSON.stringify({ groupId, title, url, rankLabel, sortOrder }),
  });
}

export async function deleteLinkGroupItemApi(itemId) {
  const token = window.localStorage.getItem('accessToken');
  return request(`/link-group-items/${itemId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}
